import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, BadgeCheck, ExternalLink, PenLine, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Reveal } from "@/components/motion";
import { REVIEWS, BRAND } from "@/lib/data";

const Stars = ({ n = 5, size = "w-4 h-4" }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <motion.span key={i} initial={{ scale: 0, rotate: -30 }} whileInView={{ scale: 1, rotate: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
        <Star className={`${size} ${i < n ? "fill-gold text-gold" : "text-muted-foreground/30"}`} />
      </motion.span>
    ))}
  </div>
);

function ReviewText({ text }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 220;
  return (
    <p className="text-sm text-muted-foreground leading-relaxed mt-3">
      {open || !long ? text : text.slice(0, 220) + "… "}
      {long && <button onClick={() => setOpen(!open)} className="text-primary font-medium">{open ? "Read less" : "Read more"}</button>}
    </p>
  );
}

function Card({ r }) {
  return (
    <div className="glass rounded-2xl p-6 shadow-sm hover-lift break-inside-avoid border border-border/60" data-testid="review-card">
      <div className="flex items-center gap-3">
        <Avatar className="w-11 h-11">
          <AvatarFallback className="bg-primary/10 text-primary font-semibold">{r.author?.[0] || "G"}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-semibold truncate">{r.author}</p>
            {r.verified && <BadgeCheck className="w-4 h-4 text-primary shrink-0" />}
          </div>
          <p className="text-xs text-muted-foreground">{r.time}</p>
        </div>
        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Google_%22G%22_Logo.svg/512px-Google_%22G%22_Logo.svg.png" alt="Google" className="w-5 h-5 opacity-70" loading="lazy" />
      </div>
      <div className="mt-3"><Stars n={r.rating} /></div>
      <ReviewText text={r.text} />
    </div>
  );
}

export default function Reviews() {
  const data = REVIEWS;
  const [featured, setFeatured] = useState(0);

  useEffect(() => {
    if (!data.reviews.length) return;
    const t = setInterval(() => setFeatured((p) => (p + 1) % data.reviews.length), 5000);
    return () => clearInterval(t);
  }, [data.reviews.length]);

  return (
    <section id="reviews" className="py-20 md:py-32 bg-secondary/40" data-testid="reviews">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><p className="uppercase text-xs tracking-[0.25em] text-primary font-medium mb-3 text-center">Testimonials</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-3xl md:text-5xl font-bold text-center mb-10">Loved by homeowners across Bengaluru</h2></Reveal>

        <Reveal delay={0.1}>
          <div className="glass rounded-3xl p-8 mb-12 flex flex-col md:flex-row items-center justify-between gap-6 border border-border" data-testid="reviews-summary">
            <div className="flex items-center gap-5">
              <div className="text-center">
                <p className="font-display text-5xl font-bold text-primary">{Number(data.rating).toFixed(1)}</p>
                <div className="mt-1"><Stars n={Math.round(data.rating)} /></div>
              </div>
              <div className="border-l border-border pl-5">
                <p className="font-display text-2xl">Rated Excellent on Google</p>
                <p className="text-sm text-muted-foreground">{data.total}+ verified Google reviews</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild variant="outline" className="rounded-full" data-testid="view-all-google">
                <a href={BRAND.googleReviewLink} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" /> View All Google Reviews</a>
              </Button>
              <Button asChild className="rounded-full bg-primary text-white hover:bg-primary/90" data-testid="leave-review">
                <a href={BRAND.googleReviewLink} target="_blank" rel="noreferrer"><PenLine className="w-4 h-4 mr-2" /> Leave a Review</a>
              </Button>
            </div>
          </div>
        </Reveal>

        {/* Featured rotating review */}
        <Reveal delay={0.15}>
          <div className="relative max-w-3xl mx-auto mb-14 bg-charcoal text-white rounded-3xl p-8 md:p-10 grain overflow-hidden" data-testid="featured-review">
            <motion.div key={featured} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Stars n={data.reviews[featured].rating} size="w-5 h-5" />
              <p className="font-display text-xl md:text-2xl leading-relaxed mt-5">“{data.reviews[featured].text}”</p>
              <p className="mt-6 text-gold font-medium">{data.reviews[featured].author}</p>
              <p className="text-white/50 text-sm">{data.reviews[featured].time}</p>
            </motion.div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setFeatured((p) => (p - 1 + data.reviews.length) % data.reviews.length)} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setFeatured((p) => (p + 1) % data.reviews.length)} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </Reveal>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
          {data.reviews.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}>
              <Card r={r} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
